import { useEffect, useRef } from 'react';
import { useSAPDataStore } from '../store/SAPDataStore';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { natsClient } from '../services/NATSClient';

/**
 * Simple refresh hook with only two triggers:
 * 1. When there's no SAP data (initial load)
 * 2. When user hits browser refresh button (F5/Ctrl+R)
 * 
 * IMPORTANT: This hook waits for NATS connection before attempting refresh
 */
export const useAppRefresh = () => {
  const { token, authenticated } = useUnifiedAuth();
  const forceRefresh = useSAPDataStore(state => state.forceRefresh);
  const hasValidData = useSAPDataStore(state => state.hasValidData);
  const loading = useSAPDataStore(state => state.loading);
  const hasInitialized = useRef(false);
  const isRefreshing = useRef(false);
  const isBrowserRefresh = useRef(false);

  // Detect browser refresh on page load
  useEffect(() => {
    // Check if this is a browser refresh (F5/Ctrl+R)
    if (performance.navigation && performance.navigation.type === 1) {
      isBrowserRefresh.current = true;
    } else if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntries.length > 0 && navEntries[0].type === 'reload') {
        isBrowserRefresh.current = true;
      }
    }
  }, []);

  // Simple refresh function
  const refreshData = async (reason: string) => {
    if (isRefreshing.current || loading) {
      return;
    }

    if (!authenticated || !token) {
      return;
    }

    // Check NATS connection before attempting refresh
    if (!natsClient.isConnected()) {
      return;
    }

    try {
      isRefreshing.current = true;
      await forceRefresh(token); // Use same method as Header button
    } catch (error) {
      console.error(`AppRefresh: Refresh failed (${reason}):`, error);
    } finally {
      isRefreshing.current = false;
    }
  };

  // Main effect: refresh when authenticated and either no data OR browser refresh
  useEffect(() => {
    const natsConnected = natsClient.isConnected();

    if (!hasInitialized.current && authenticated && token && natsConnected) {
      hasInitialized.current = true;
      
      const noData = !hasValidData();
      const shouldRefresh = noData || isBrowserRefresh.current;
      
      if (shouldRefresh) {
        const reason = noData ? 'no SAP data' : 'browser refresh';
        refreshData(reason);
      }
    }
  }, [authenticated, token, hasValidData]);

  // Monitor NATS connection and retry refresh when it becomes available
  useEffect(() => {
    if (!authenticated || !token || hasInitialized.current) {
      return; // No need to monitor if not authenticated or already initialized
    }

    const checkNATSAndRefresh = () => {
      if (natsClient.isConnected()) {
        const noData = !hasValidData();
        const shouldRefresh = noData || isBrowserRefresh.current;
        
        if (shouldRefresh) {
          hasInitialized.current = true;
          const reason = noData ? 'no SAP data (NATS ready)' : 'browser refresh (NATS ready)';
          refreshData(reason);
        }
      }
    };

    // Check immediately
    checkNATSAndRefresh();

    // Check periodically until NATS is connected or we're initialized
    const interval = setInterval(checkNATSAndRefresh, 2000); // Check every 2 seconds
    
    return () => clearInterval(interval);
  }, [authenticated, token, hasValidData]);
};
